import React, { useCallback, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { BorderCondition, SubCondition } from 'dorian-shared/types/branch/HubOption';
import { ConditionQuantifier, HUB_MAX_SUB_CONDITIONS } from 'dorian-shared/types/branch/HubOption';
import type { MemoryDTO } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { DragHandle } from 'components/ui/DragHandle/DragHandle';
import { SubConditionRow } from '../SubConditionRow';
import { createEmptySubCondition } from '../../stateConditionDefaults';
import { BorderColorInput } from './BorderColorInput';
import './BorderConditionCard.scss';

interface BorderConditionCardProps {
  condition: BorderCondition;
  memoryVariables: MemoryDTO[];
  isDisabled: boolean;
  isValid: boolean;
  onChange: (uuid: string, updated: BorderCondition) => void;
  onRemove: (uuid: string) => void;
}

function BorderConditionCardUnmemoized({
  condition, memoryVariables, isDisabled, isValid, onChange, onRemove,
}: BorderConditionCardProps) {
  const handleChange = useCallback(
    (updated: BorderCondition) => onChange(condition.uuid, updated),
    [onChange, condition.uuid],
  );
  const handleRemove = useCallback(
    () => onRemove(condition.uuid),
    [onRemove, condition.uuid],
  );
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({
    id: condition.uuid,
    disabled: isDisabled,
    transition: { duration: 250, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' },
  });

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }),
    [transform, transition, isDragging],
  );

  const handleQuantifierChange = useCallback(
    (q: ConditionQuantifier) => handleChange({ ...condition, quantifier: q }),
    [condition, handleChange],
  );

  const handleColorChange = useCallback(
    (color: string | null) => handleChange({ ...condition, color: color ?? '' }),
    [condition, handleChange],
  );

  const handleSubConditionChange = useCallback(
    (index: number, updated: SubCondition) => {
      const next = [...condition.subConditions];
      next[index] = updated;
      handleChange({ ...condition, subConditions: next });
    },
    [condition, handleChange],
  );

  const handleSubConditionRemove = useCallback(
    (index: number) => {
      if (condition.subConditions.length <= 1) return;
      const next = condition.subConditions.filter((_, i) => i !== index);
      handleChange({ ...condition, subConditions: next });
    },
    [condition, handleChange],
  );

  const handleAddSubCondition = useCallback(() => {
    if (condition.subConditions.length >= HUB_MAX_SUB_CONDITIONS) return;
    handleChange({
      ...condition,
      subConditions: [...condition.subConditions, createEmptySubCondition()],
    });
  }, [condition, handleChange]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border-condition-card p-2${!isValid ? ' border-condition-card--error' : ''}`}
      data-testid="border-condition-card"
    >
      <div className="d-flex align-items-start">
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          disabled={isDisabled}
          className="mr-1"
        />

        <div className="flex-grow-1">
          <div className="d-flex align-items-center mb-1">
            <label className="mb-0 small mr-2" style={{ fontWeight: 600 }}>show when:</label>
          </div>

          <div className="border-condition-card__conditions">
            {condition.subConditions.length > 1 && (
              <div className="d-flex align-items-center mb-2">
                <label className="mb-0 small mr-1" style={{ fontWeight: 600 }}>When</label>
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className={`btn btn-sm ${condition.quantifier === ConditionQuantifier.All ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handleQuantifierChange(ConditionQuantifier.All)}
                    disabled={isDisabled}
                  >
                    ALL
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${condition.quantifier === ConditionQuantifier.Any ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handleQuantifierChange(ConditionQuantifier.Any)}
                    disabled={isDisabled}
                  >
                    ANY
                  </button>
                </div>
                <label className="mb-0 small ml-1">of these are true:</label>
              </div>
            )}

            {condition.subConditions.map((sub, idx) => (
              <SubConditionRow
                key={sub.uuid}
                subCondition={sub}
                memoryVariables={memoryVariables}
                isDisabled={isDisabled}
                canRemove={condition.subConditions.length > 1}
                onChange={(updated) => handleSubConditionChange(idx, updated)}
                onRemove={() => handleSubConditionRemove(idx)}
              />
            ))}

            {condition.subConditions.length < HUB_MAX_SUB_CONDITIONS && (
              <button
                type="button"
                className="btn btn-outline-info btn-sm mt-2"
                style={{ fontSize: '0.7rem' }}
                onClick={handleAddSubCondition}
                disabled={isDisabled}
              >
                + Sub-condition
              </button>
            )}

            <div className="d-flex align-items-center mt-2 border-condition-card__result">
              <span className="small mr-2">→ Border color</span>
              <BorderColorInput
                value={condition.color || null}
                onChange={handleColorChange}
                disabled={isDisabled}
                testIdPrefix="border-condition-color"
              />
            </div>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-outline-danger btn-sm ml-2"
          onClick={handleRemove}
          disabled={isDisabled}
          title="Remove border override"
        >
          ×
        </button>
      </div>
    </div>
  );
}

export const BorderConditionCard = React.memo(BorderConditionCardUnmemoized);
